import { z } from 'zod';
import {
  BaseResponseSchema,
  dateSchema,
  uuidSchema,
} from './primitives.js';

// ============================================================================
// SEARCH SCHEMAS (Searchable Encryption System)
// ============================================================================

// Entity types that can be searched
export const SearchableEntitySchema = z.enum([
  'Container',
  'Customer',
  'Group',
  'Invoice',
  'LocationHistory',
  'TenantUser',
  'Tenant',
  'Wafer',
  'WaferConflict',
  'WaferTransferHistory',
  'ProviderUser',
  'EncryptionKey'
]);

// Search options schema
export const SearchOptionsSchema = z.object({
  limit: z.number().int().min(1).max(100).optional().default(50),
  offset: z.number().int().min(0).optional().default(0),
  minScore: z.number().min(0).max(1).optional().default(0.1),
  exactMatchBoost: z.number().min(1).optional().default(2.0),
  includeDeleted: z.boolean().optional().default(false),
  sortBy: z.enum(['score', 'createdAt', 'updatedAt']).optional().default('score'),
  sortDirection: z.enum(['ASC', 'DESC']).optional().default('DESC')
});

// Fuzzy search options schema
export const FuzzySearchOptionsSchema = SearchOptionsSchema.extend({
  maxEditDistance: z.number().int().min(1).max(3).optional().default(2),
  phoneticMatch: z.boolean().optional().default(false)
});

// Multi-field search options schema
export const MultiFieldSearchOptionsSchema = SearchOptionsSchema.extend({
  fieldWeights: z.record(z.number().min(0)).optional(),
  requireAll: z.boolean().optional().default(false)
});

// Universal search options schema
export const UniversalSearchOptionsSchema = SearchOptionsSchema.extend({
  entityTypes: z.array(SearchableEntitySchema).optional(), // Limit to specific entity types
  maxResultsPerEntity: z.number().int().min(1).max(50).optional().default(10)
});

// Search result metadata schema
export const SearchResultMetadataSchema = z.object({
  isExactMatch: z.boolean(),
  matchPercentage: z.number().min(0).max(1),
  createdAt: dateSchema.optional(),
  updatedAt: dateSchema.optional()
});

// Search result schema
export const SearchResultSchema = z.object({
  entityType: SearchableEntitySchema,
  entityId: z.string(),
  tenantId: uuidSchema.optional(),
  fieldName: z.string(),
  entity: z.any().optional(), // Decrypted entity object
  score: z.number().min(0).max(1),
  matchedNgrams: z.array(z.string()),
  highlightedValue: z.string().optional(),
  metadata: SearchResultMetadataSchema.optional()
});

// Grouped search results schema
export const GroupedSearchResultsSchema = z.object({
  resultsByType: z.record(z.array(SearchResultSchema)),
  totalResults: z.number().int(),
  query: z.string(),
  executionTime: z.number(), // milliseconds
  pagination: z.object({
    limit: z.number().int(),
    offset: z.number().int(),
    hasMore: z.boolean()
  })
});

// ==================== REQUEST SCHEMAS ====================

// Simple search request
export const SimpleSearchRequestSchema = z.object({
  query: z.string().min(2).max(200).trim(),
  entityType: SearchableEntitySchema,
  fieldName: z.string().min(1).max(100),
  options: SearchOptionsSchema.optional()
});

// Fuzzy search request
export const FuzzySearchRequestSchema = z.object({
  query: z.string().min(2).max(200).trim(),
  entityType: SearchableEntitySchema,
  fieldName: z.string().min(1).max(100),
  options: FuzzySearchOptionsSchema.optional()
});

// Multi-field search request
export const MultiFieldSearchRequestSchema = z.object({
  query: z.string().min(2).max(200).trim(),
  entityType: SearchableEntitySchema,
  fieldNames: z.array(z.string().min(1).max(100)).min(1),
  options: MultiFieldSearchOptionsSchema.optional()
});

// Universal search request
export const UniversalSearchRequestSchema = z.object({
  query: z.string().min(2).max(200).trim(),
  options: UniversalSearchOptionsSchema.optional()
});

// ==================== RESPONSE SCHEMAS ====================

// Simple search response
export const SimpleSearchResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    results: z.array(SearchResultSchema),
    query: z.string(),
    totalResults: z.number().int(),
    executionTime: z.number(),
    pagination: z.object({
      limit: z.number().int(),
      offset: z.number().int(),
      hasMore: z.boolean()
    })
  })
});

// Fuzzy search response
export const FuzzySearchResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    results: z.array(SearchResultSchema),
    query: z.string(),
    totalResults: z.number().int(),
    executionTime: z.number(),
    fuzzyMatchUsed: z.boolean(),
    pagination: z.object({
      limit: z.number().int(),
      offset: z.number().int(),
      hasMore: z.boolean()
    })
  })
});

// Multi-field search response
export const MultiFieldSearchResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    results: z.array(SearchResultSchema),
    query: z.string(),
    totalResults: z.number().int(),
    executionTime: z.number(),
    fieldsSearched: z.array(z.string()),
    pagination: z.object({
      limit: z.number().int(),
      offset: z.number().int(),
      hasMore: z.boolean()
    })
  })
});

// Universal search response
export const UniversalSearchResponseSchema = BaseResponseSchema.extend({
  data: GroupedSearchResultsSchema
});

// ==================== TYPE EXPORTS ====================

// Request types
// Inferred types
export type FuzzySearchOptions = z.infer<typeof FuzzySearchOptionsSchema>;
export type FuzzySearchRequest = z.infer<typeof FuzzySearchRequestSchema>;
export type FuzzySearchResponse = z.infer<typeof FuzzySearchResponseSchema>;
export type FuzzySearchResponseData = z.infer<typeof FuzzySearchResponseSchema.shape.data>;
export type GroupedSearchResults = z.infer<typeof GroupedSearchResultsSchema>;
export type MultiFieldSearchOptions = z.infer<typeof MultiFieldSearchOptionsSchema>;
export type MultiFieldSearchRequest = z.infer<typeof MultiFieldSearchRequestSchema>;
export type MultiFieldSearchResponse = z.infer<typeof MultiFieldSearchResponseSchema>;
export type MultiFieldSearchResponseData = z.infer<typeof MultiFieldSearchResponseSchema.shape.data>;
export type SearchOptions = z.infer<typeof SearchOptionsSchema>;
export type SearchResult = z.infer<typeof SearchResultSchema>;
export type SearchResultMetadata = z.infer<typeof SearchResultMetadataSchema>;
export type SearchableEntity = z.infer<typeof SearchableEntitySchema>;
export type SimpleSearchRequest = z.infer<typeof SimpleSearchRequestSchema>;
export type SimpleSearchResponse = z.infer<typeof SimpleSearchResponseSchema>;
export type SimpleSearchResponseData = z.infer<typeof SimpleSearchResponseSchema.shape.data>;
export type UniversalSearchOptions = z.infer<typeof UniversalSearchOptionsSchema>;
export type UniversalSearchRequest = z.infer<typeof UniversalSearchRequestSchema>;
export type UniversalSearchResponse = z.infer<typeof UniversalSearchResponseSchema>;
export type UniversalSearchResponseData = z.infer<typeof UniversalSearchResponseSchema.shape.data>;
